import { Divider, Text } from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerMessageDialogStyles } from './CustomerMessageDialog.styles';
import { PassengerIcon } from '../../Icons/PassengerIcon';
import CustomerUpdatesDialog, {
    CustomerUpdatesDialogView,
} from '../components/CustomerUpdatesDialog/CustomerUpdatesDialog';
import {
    CustomerMessage,
    CustomerWithStatuses,
} from '../../../model/customerWithUpdates';
import {
    addCustomerMessageFeedback,
    fetchCustomerMessages,
} from '../../../store/features/customerUpdates/customerUpdates.api';
import { ICustomerNotificationActionContext } from '../../../store/features/customerUpdates/customerUpdates.types';
import { useCustomerUpdatesDialogResultCallbacks } from '../components/CustomerUpdatesDialog/useCustomerUpdatesDialogResultCallback';
import ItemList, { ItemTab, TabId } from '../components/ItemList/ItemList';
import { formatCompactDateTime } from '../../../utils/formatters';
import MailIcon from '../../Icons/MailIcon';
import { customerItemStatusToIconType } from '../../Icons/StyledIcon';

interface CustomerMessageDialogProps {
    isFlightTeamBoard: boolean;
    open?: boolean;
    customerInfo: CustomerWithStatuses | null;
    onResultClick?: (context: ICustomerNotificationActionContext) => void;
    onOpenChange?: (open: boolean) => void;
}

const MessageContent: React.FC<{ selectedMessage: CustomerMessage }> = ({
    selectedMessage,
}) => {
    const styles = useCustomerMessageDialogStyles();

    const { t } = useTranslation();

    return (
        <div className={styles.content}>
            <div className={styles.field}>
                <Text weight="bold">{t('Message')}</Text>
                {selectedMessage.content}
            </div>
            {selectedMessage.feedback && (
                <>
                    <Divider />
                    <div className={styles.field}>
                        <Text weight="semibold">{t('Feedback')}</Text>
                        <Text italic>{selectedMessage.feedback}</Text>
                    </div>
                </>
            )}
        </div>
    );
};

const CustomerMessageDialog = ({
    isFlightTeamBoard,
    open,
    customerInfo,
    onResultClick,
    onOpenChange,
}: CustomerMessageDialogProps) => {
    const customer = customerInfo?.customer;
    const flightTeam = customerInfo?.flightTeam;

    const styles = useCustomerMessageDialogStyles();

    const { t } = useTranslation();

    const [dialogView, setDialogView] =
        useState<CustomerUpdatesDialogView>('initial');
    const [isMobile, setIsMobile] = useState(false);

    const [messages, setMessages] = useState<CustomerMessage[]>([]);
    const [selectedMessage, setSelectedMessage] =
        useState<CustomerMessage | null>(null);

    const notificationStatus = selectedMessage?.notificationInfo.status;

    const onMessageSelected = useCallback(
        (id: TabId) => {
            setSelectedMessage(
                messages.find((x) => x.messageId === id) ?? null
            );
            setDialogView('initial');
        },
        [messages, setSelectedMessage]
    );

    const loadCustomerMessages = useCallback(
        (customerId: number) =>
            fetchCustomerMessages(customerId).then((x) => {
                const messages = [...x.data];
                setMessages(messages);

                if (!messages.length) {
                    return;
                }

                setSelectedMessage(messages[0]);
            }),
        [setMessages]
    );

    useEffect(() => {
        setMessages([]);

        if (!customer) {
            return;
        }

        loadCustomerMessages(customer.id);
    }, [customer, loadCustomerMessages]);

    useEffect(() => {
        setDialogView('initial');
    }, [selectedMessage]);

    const { onNotified, onUnableToNotify } =
        useCustomerUpdatesDialogResultCallbacks(
            'CustomerMessages',
            customer?.id,
            onResultClick
        );

    const onMessageFeedback = useMemo(() => {
        // Return undefined if loading or feedback already provided.
        if (!selectedMessage || !customer || selectedMessage.feedback) {
            return;
        }

        return (value: string) => {
            if (!value) {
                return;
            }

            addCustomerMessageFeedback(selectedMessage.messageId, {
                feedback: value,
            })
                .then(() => loadCustomerMessages(customer.id))
                .then();
        };
    }, [customer, selectedMessage, loadCustomerMessages]);

    const messageTabs = useMemo<ItemTab[]>(
        () =>
            messages.map((x) => ({
                id: x.messageId,
                label: formatCompactDateTime(x.sentOnUtc),
            })),
        [messages]
    );

    const messageContent = selectedMessage ? (
        <MessageContent selectedMessage={selectedMessage} />
    ) : undefined;

    const messageIcon = useMemo(
        () =>
            notificationStatus && !isMobile ? (
                <MailIcon
                    type={customerItemStatusToIconType(notificationStatus)}
                    className={styles.icon}
                />
            ) : undefined,
        [notificationStatus, isMobile, styles]
    );

    const content =
        messages && flightTeam ? (
            <ItemList
                tabs={messageTabs}
                selectedTabId={selectedMessage?.messageId ?? null}
                content={messageContent}
                title={t('CustomerMessagesList_Title')}
                tabLabelPrefix={t('CustomerMessagesList_SentOn')}
                icon={messageIcon}
                onTabSelected={onMessageSelected}
                onIsMobileChange={setIsMobile}
            />
        ) : undefined;

    return (
        <CustomerUpdatesDialog
            open={open}
            customer={customer}
            status={notificationStatus}
            icon={<PassengerIcon />}
            content={content}
            view={dialogView}
            isFlightTeamBoard={isFlightTeamBoard}
            onOpenChange={onOpenChange}
            onNotified={onNotified}
            onUnableToNotify={onUnableToNotify}
            onMessageFeedback={onMessageFeedback}
            onViewChange={setDialogView}
        />
    );
};

export default CustomerMessageDialog;
